import { useNavigate } from "react-router-dom";
import { Pencil } from "lucide-react";

import { PermissionGate } from "@/components/common/PermissionGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/utils";
import type { ResourceWithoutBillingCycle } from "@/types/billing-cycle";

interface ResourcesWithoutCycleTableProps {
  rows: ResourceWithoutBillingCycle[];
}

/**
 * How much term is left — which is how much longer the machine is served with nothing billing it.
 */
function TermRemainingCell({ row }: { row: ResourceWithoutBillingCycle }) {
  const days = row.daysRemaining;

  return (
    <Badge
      variant="outline"
      className="border-destructive/30 bg-destructive-soft text-destructive-ink"
    >
      {days <= 0
        ? "Ends today"
        : `${days} day${days === 1 ? "" : "s"} left`}
    </Badge>
  );
}

/**
 * Machines still inside their term that no active billing cycle accounts for.
 *
 * The sibling of the drift report, and the case that one structurally cannot state: drift compares
 * a machine's expiry against its cycle end, which needs both, so a machine with no cycle drops out
 * of it rather than becoming a finding. Nothing repairs these either — the nightly reconciliation
 * only moves a resource onto a cycle that already exists.
 *
 * Scoped to machines whose expiry is still ahead: one whose term elapsed is meant to have no live
 * cycle, and one that never had an expiry was never wired to billing at all. Neither is a machine
 * being served right now on something nobody is billing, which is what this is for.
 *
 * Read-only, for the same reason the drift report is: opening a cycle creates a paid-through record
 * and closing the machine destroys one, and neither is a decision a report should make. Edit leads
 * to the machine's own expiry screen, where an operator can set the term deliberately.
 *
 * `lastCycleEnd` is what separates the two rows that look alike here: a machine whose term simply
 * ran out has one, and a machine that was never wired to billing has none.
 */
export function ResourcesWithoutCycleTable({
  rows,
}: ResourcesWithoutCycleTableProps) {
  const navigate = useNavigate();

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-md border">
      <Table className="border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
        <TableHeader className="sticky top-0 z-30 bg-card">
          <TableRow>
            <TableHead className="w-12 text-center">STT</TableHead>
            <TableHead>VM Name</TableHead>
            <TableHead>VM UUID</TableHead>
            <TableHead>User</TableHead>
            <TableHead className="text-center">Resource expiry</TableHead>
            <TableHead className="text-center">Last cycle</TableHead>
            <TableHead>Term left</TableHead>
            <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.resourceId}>
              <TableCell className="text-center text-muted-foreground">
                {index + 1}
              </TableCell>
              <TableCell>
                <div className="max-w-56 truncate font-medium">
                  {row.resourceName}
                </div>
                <div className="text-xs text-muted-foreground">
                  {row.resourceStatus}
                </div>
              </TableCell>

              {/* Shown whole, not truncated: this is the id an operator pastes into WiService or a
                  ticket, and half of one is no use for either. */}
              <TableCell className="whitespace-nowrap font-mono text-xs">
                {row.providerResourceId ?? (
                  <span className="font-sans text-muted-foreground">—</span>
                )}
              </TableCell>
              {/* Name, email and phone, the same three lines the payments queue shows: this is a
                  worklist, and the operator who picks a row up calls the customer about it. */}
              <TableCell className="max-w-56">
                <div className="truncate font-medium">
                  {row.userName || "—"}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {row.userEmail || "—"}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {row.userPhone || "—"}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap text-center">
                {formatDateTime(row.expiresAt)}
              </TableCell>

              {/* The column that tells the two lookalike rows apart: a term that simply ran out has
                  a last cycle, a machine that was never wired to billing has none. */}
              <TableCell className="whitespace-nowrap text-center">
                {row.lastCycleEnd ? (
                  <>
                    <div>{formatDateTime(row.lastCycleEnd)}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.lastCycleStatus}
                    </div>
                  </>
                ) : (
                  <span className="text-muted-foreground">Never had one</span>
                )}
              </TableCell>
              <TableCell>
                <TermRemainingCell row={row} />
              </TableCell>
              <TableCell className="sticky right-0 z-10 bg-card shadow-[inset_1px_0_0_0_var(--border)]">
                <div className="flex items-center justify-center">
                  {/* Same gate as the drift report: the expiry screen behind this is
                      SuperAdmin-only, so showing the button to a BillingAdmin or SupportAdmin who
                      can read the report would only send them into a 403 route. */}
                  <PermissionGate roles="SuperAdmin">
                    <Button
                      variant="outline"
                      size="sm"
                      className="cursor-pointer gap-1"
                      // No cycle id to carry, unlike the drift report: that is the finding.
                      onClick={() =>
                        navigate(`/admin/resources/${row.resourceId}/expiry`)
                      }
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Set expiry
                    </Button>
                  </PermissionGate>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
